import type { ThemeConfig } from 'antd';
import { themeColors, componentSize, type ComponentSize } from './theme-config';

export const createAntdTheme = (): ThemeConfig => ({
  token: {
    colorPrimary: themeColors.primary,
    colorPrimaryHover: themeColors.primaryHover,
    colorPrimaryActive: themeColors.primaryActive,
    colorTextPlaceholder: themeColors.placeholder,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  components: {
    Select: {
      colorError: themeColors.danger,
      colorErrorBorder: themeColors.danger,
      colorErrorHover: themeColors.dangerHover,
      colorErrorOutline: `rgba(${themeColors.danger}, 0.2)`,
    },

    Button: {
      primaryColor: '#ffffff',
      primaryShadow: `0 2px 4px ${themeColors.primary}33`,
    },

    Input: {
      activeBorderColor: themeColors.primary,
      hoverBorderColor: themeColors.primaryHover,
      activeShadow: `0 0 0 2px ${themeColors.primary}1A`,
      colorError: themeColors.danger,
      colorErrorBorder: themeColors.danger,
      colorErrorHover: themeColors.dangerHover,
      colorErrorOutline: `rgba(${themeColors.danger}, 0.2)`,
    },

    DatePicker: {
      activeBorderColor: themeColors.primary,
      hoverBorderColor: themeColors.primaryHover,
      activeShadow: `0 0 0 2px ${themeColors.primary}1A`,
      colorError: themeColors.danger,
      colorErrorBorder: themeColors.danger,
      colorErrorHover: themeColors.dangerHover,
      colorErrorOutline: `rgba(${themeColors.danger}, 0.2)`,
    },

    Switch: {
      colorPrimary: themeColors.primary,
      colorPrimaryHover: themeColors.primaryHover,
    },

    Table: {
      rowHoverBg: `${themeColors.primary}0A`,
      rowSelectedBg: `${themeColors.primary}14`,
      rowSelectedHoverBg: `${themeColors.primary}1F`,
    },

    Upload: {
      colorPrimary: themeColors.primary,
      colorPrimaryHover: themeColors.primaryHover,
      actionsColor: themeColors.primary,
      colorError: themeColors.danger,
      colorErrorBorder: themeColors.danger,
      colorErrorHover: themeColors.dangerHover,
      colorErrorOutline: `rgba(${themeColors.danger}, 0.2)`,
    },

    Tabs: {
      colorPrimary: themeColors.primary,
      colorPrimaryHover: themeColors.primaryHover,
      colorPrimaryActive: themeColors.primaryActive,
      itemColor: '#9CA3AF', // Gray color for inactive tabs
      itemHoverColor: themeColors.primary,
      itemSelectedColor: themeColors.primary,
      inkBarColor: themeColors.primary,
      titleFontSize: 16,
      titleFontSizeLG: 16,
      titleFontSizeSM: 14,
      horizontalMargin: '0',
      horizontalItemPadding: '12px 24px',
    },

    Radio: {
      colorPrimary: themeColors.primary,
      colorPrimaryHover: themeColors.primaryHover,
      colorPrimaryActive: themeColors.primaryActive,
      dotColorDisabled: '#d9d9d9',
    },

    Drawer: {
      colorBgElevated: '#ffffff',
      colorBgMask: 'rgba(0, 0, 0, 0.45)',
    },

    Typography: {
      colorText: '#1f2937',
      colorTextHeading: '#1f2937',
      colorTextSecondary: '#6b7280',
    },
  },
});

export const createPrimaryTheme = (): ThemeConfig => {
  return {
    ...createAntdTheme(),
    components: {
      ...createAntdTheme().components,
      Button: {
        ...createAntdTheme().components?.Button,
        controlHeight: parseInt(componentSize.height),
        fontSize: parseInt(componentSize.fontSize),

        textHoverBg: `${themeColors.primary}0A`,

        defaultColor: themeColors.primary,
        defaultBorderColor: themeColors.primary,
        defaultHoverColor: themeColors.primaryHover,
        defaultHoverBorderColor: themeColors.primaryHover,
        defaultActiveColor: themeColors.primaryActive,
        defaultActiveBorderColor: themeColors.primaryActive,
      },
      Select: {
        controlHeight: parseInt(componentSize.height),
        fontSize: parseInt(componentSize.fontSize),
        controlPaddingHorizontal: parseInt(componentSize.padding.split(' ')[1]),
      },
      Input: {
        ...createAntdTheme().components?.Input,
        controlHeight: parseInt(componentSize.height),
        fontSize: parseInt(componentSize.fontSize),
        paddingInline: parseInt(componentSize.padding.split(' ')[1]),
      },
      DatePicker: {
        ...createAntdTheme().components?.DatePicker,
        controlHeight: parseInt(componentSize.height),
        fontSize: parseInt(componentSize.fontSize),
      },
      Upload: {
        ...createAntdTheme().components?.Upload,
        fontSize: parseInt(componentSize.fontSize),
      },
      Tabs: {
        ...createAntdTheme().components?.Tabs,
        fontSize: parseInt(componentSize.fontSize),
      },
      Radio: {
        ...createAntdTheme().components?.Radio,
        fontSize: parseInt(componentSize.fontSize),
      },
      Drawer: {
        ...createAntdTheme().components?.Drawer,
      },
      Typography: {
        ...createAntdTheme().components?.Typography,
        fontSize: parseInt(componentSize.fontSize),
      },
    },
  };
};

export const createSecondaryTheme = (): ThemeConfig => {
  return {
    ...createAntdTheme(),
    token: {
      ...createAntdTheme().token,
      colorPrimary: themeColors.buttonSecondaryBackground,
      colorPrimaryHover: themeColors.buttonSecondaryBackgroundHover,
      colorPrimaryActive: themeColors.buttonSecondaryBackgroundHover,
      colorText: themeColors.buttonSecondaryText,
    },
    components: {
      ...createAntdTheme().components,
      Button: {
        ...createAntdTheme().components?.Button,
        controlHeight: parseInt(componentSize.height),
        fontSize: parseInt(componentSize.fontSize),
        defaultBg: themeColors.buttonSecondaryBackground,
        defaultBorderColor: themeColors.buttonSecondaryBorder,
        defaultColor: themeColors.buttonSecondaryText,
        defaultHoverBg: themeColors.buttonSecondaryBackgroundHover,
        defaultHoverBorderColor: themeColors.buttonSecondaryBorder,
        defaultHoverColor: themeColors.buttonSecondaryText,
        defaultActiveBg: themeColors.buttonSecondaryBackgroundHover,
        defaultActiveBorderColor: themeColors.buttonSecondaryBorder,
        defaultActiveColor: themeColors.buttonSecondaryText,
      },
      Select: {
        controlHeight: parseInt(componentSize.height),
        fontSize: parseInt(componentSize.fontSize),
        controlPaddingHorizontal: parseInt(componentSize.padding.split(' ')[1]),
      },
      Input: {
        controlHeight: parseInt(componentSize.height),
        fontSize: parseInt(componentSize.fontSize),
        paddingInline: parseInt(componentSize.padding.split(' ')[1]),
      },
      DatePicker: {
        ...createAntdTheme().components?.DatePicker,
        controlHeight: parseInt(componentSize.height),
        fontSize: parseInt(componentSize.fontSize),
      },
      Upload: {
        ...createAntdTheme().components?.Upload,
        fontSize: parseInt(componentSize.fontSize),
      },
      Tabs: {
        ...createAntdTheme().components?.Tabs,
        fontSize: parseInt(componentSize.fontSize),
      },
      Radio: {
        ...createAntdTheme().components?.Radio,
        fontSize: parseInt(componentSize.fontSize),
      },
      Drawer: {
        ...createAntdTheme().components?.Drawer,
      },
      Typography: {
        ...createAntdTheme().components?.Typography,
        fontSize: parseInt(componentSize.fontSize),
      },
    },
  };
};

export const createDangerTheme = (): ThemeConfig => {
  return {
    ...createAntdTheme(),
    token: {
      ...createAntdTheme().token,
      colorPrimary: themeColors.danger,
      colorPrimaryHover: themeColors.dangerHover,
      colorPrimaryActive: themeColors.dangerHover,
      colorText: '#ffffff',
    },
    components: {
      ...createAntdTheme().components,
      Button: {
        ...createAntdTheme().components?.Button,
        controlHeight: parseInt(componentSize.height),
        fontSize: parseInt(componentSize.fontSize),
        primaryColor: '#ffffff',
        primaryShadow: `0 2px 4px ${themeColors.danger}33`,
      },
      Select: {
        controlHeight: parseInt(componentSize.height),
        fontSize: parseInt(componentSize.fontSize),
        controlPaddingHorizontal: parseInt(componentSize.padding.split(' ')[1]),
      },
      Input: {
        controlHeight: parseInt(componentSize.height),
        fontSize: parseInt(componentSize.fontSize),
        paddingInline: parseInt(componentSize.padding.split(' ')[1]),
      },
      DatePicker: {
        ...createAntdTheme().components?.DatePicker,
        controlHeight: parseInt(componentSize.height),
        fontSize: parseInt(componentSize.fontSize),
      },
      Upload: {
        ...createAntdTheme().components?.Upload,
        fontSize: parseInt(componentSize.fontSize),
      },
      Tabs: {
        ...createAntdTheme().components?.Tabs,
        fontSize: parseInt(componentSize.fontSize),
      },
      Radio: {
        ...createAntdTheme().components?.Radio,
        fontSize: parseInt(componentSize.fontSize),
      },
      Drawer: {
        ...createAntdTheme().components?.Drawer,
      },
      Typography: {
        ...createAntdTheme().components?.Typography,
        fontSize: parseInt(componentSize.fontSize),
      },
    },
  };
};
